// Hybrid Image Service - Frontend generation + Backend APIs
import { generateImages } from './imageGenerationService';

// Get the correct backend URL
const API_BASE = process.env.NODE_ENV === 'production' 
  ? 'https://renovate-library-service-jo572.ondigitalocean.app'
  : 'http://localhost:8000';

/**
 * Hybrid approach: Frontend image generation + Backend everything else
 * This replaces the backend /api/workshop/generate endpoint with frontend generation
 * but keeps all other backend APIs intact
 */
export const generateWorkshopImages = async (formData) => {
  try {
    console.log('🎨 Hybrid: Generating images with frontend Gemini API...');
    
    // Use frontend direct Gemini generation
    const result = await generateImages(formData);
    
    console.log('✅ Hybrid: Frontend generation complete, returning backend-compatible format');
    
    return {
      images: result.images,
      total_generated: result.total_generated,
      prompt: result.prompt,
      category: result.category,
      generation_time: result.generation_time,
      success: true,
      message: `Generated ${result.total_generated} images successfully`
    };
    
  } catch (error) {
    console.error('❌ Hybrid: Frontend generation failed:', error);
    throw error;
  }
};

/**
 * Save material using backend API (unchanged)
 */
export const saveMaterial = async (materialData) => {
  try {
    console.log('💾 Hybrid: Saving material via backend API...');
    
    const response = await fetch(`${API_BASE}/api/workshop/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(materialData)
    });
    
    if (!response.ok) {
      throw new Error(`Backend save failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('✅ Hybrid: Material saved via backend');
    
    return result;
    
  } catch (error) {
    console.error('❌ Hybrid: Backend save failed:', error);
    throw error;
  }
};

/**
 * Load materials using backend API (unchanged)
 */
export const loadMaterials = async (params = {}) => {
  try {
    console.log('📦 Hybrid: Loading materials via backend API...');
    
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${API_BASE}/api/catalog/materials?${queryParams}`);
    
    if (!response.ok) {
      throw new Error(`Backend load failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('✅ Hybrid: Materials loaded via backend');
    
    return result;
    
  } catch (error) {
    console.error('❌ Hybrid: Backend load failed:', error);
    throw error;
  }
};

/**
 * Load stats using backend API (unchanged)
 */
export const loadStats = async () => {
  try {
    console.log('📊 Hybrid: Loading stats via backend API...');
    
    const response = await fetch(`${API_BASE}/api/stats`);
    
    if (!response.ok) {
      throw new Error(`Backend stats failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('✅ Hybrid: Stats loaded via backend');
    
    return result;
    
  } catch (error) {
    console.error('❌ Hybrid: Backend stats failed:', error);
    throw error;
  }
};

/**
 * Load inspiration images using backend API (unchanged)
 */
export const loadInspirationImages = async () => {
  try {
    console.log('🎨 Hybrid: Loading inspiration images via backend API...');
    
    const response = await fetch(`${API_BASE}/api/inspiration-hub/images`);
    
    if (!response.ok) {
      throw new Error(`Backend inspiration load failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('✅ Hybrid: Inspiration images loaded via backend');
    
    return result;
    
  } catch (error) {
    console.error('❌ Hybrid: Backend inspiration load failed:', error);
    throw error;
  }
};

/**
 * Save inspiration image using backend API (unchanged)
 */
export const saveInspirationImage = async (inspirationData) => {
  try {
    console.log('💾 Hybrid: Saving inspiration image via backend API...');
    
    const response = await fetch(`${API_BASE}/api/inspiration-hub/images`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inspirationData)
    });
    
    if (!response.ok) {
      throw new Error(`Backend inspiration save failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('✅ Hybrid: Inspiration image saved via backend');
    
    return result;
    
  } catch (error) {
    console.error('❌ Hybrid: Backend inspiration save failed:', error);
    throw error;
  }
};

/**
 * Archive material using backend API (unchanged)
 */
export const archiveMaterial = async (materialId) => {
  try {
    console.log('📦 Hybrid: Archiving material via backend API...');
    
    const response = await fetch(`${API_BASE}/api/catalog/materials/${materialId}/archive`, {
      method: 'PATCH'
    });
    
    if (!response.ok) {
      throw new Error(`Backend archive failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('✅ Hybrid: Material archived via backend');
    
    return result;
    
  } catch (error) {
    console.error('❌ Hybrid: Backend archive failed:', error);
    throw error;
  }
};

/**
 * Delete material using backend API (unchanged)
 */
export const deleteMaterial = async (materialId) => {
  try {
    console.log(`🗑️ Hybrid: Deleting material ${materialId} via backend API...`);
    
    const response = await fetch(`${API_BASE}/api/catalog/materials/${materialId}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Delete failed for material ${materialId}:`, {
        status: response.status,
        statusText: response.statusText,
        errorText: errorText
      });
      throw new Error(`Backend delete failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log(`✅ Hybrid: Material ${materialId} deleted via backend`);
    
    return result;
    
  } catch (error) {
    console.error(`❌ Hybrid: Backend delete failed for material ${materialId}:`, error);
    throw error;
  }
};

// Export all hybrid functions
const hybridImageService = {
  generateWorkshopImages,
  saveMaterial,
  loadMaterials,
  loadStats,
  loadInspirationImages,
  saveInspirationImage,
  archiveMaterial,
  deleteMaterial
};

export default hybridImageService;
